// ✅ AUTO-GENERATED FILE. DO NOT EDIT DIRECTLY.
// 📝 Edit the markdown files in docs/help_panels/ instead.
// 🔄 Run "npm run sync-help" from project root to update this file.

export const HELP_CONTENT: Record<string, { title: string; content: string }> = {
    "favorites": {
        "title": "Favorites Help",
        "content": `# Favorites Help

## Purpose

Your personal collection of saved wisdom. Quickly access all the Topics, Thoughts, Quotes, and Passages you've marked as favorites.

## Views & Areas

- **Title Card**: "SAVED ITEMS" with icon
- **Collapsible Sections**: Organized by content type (Topics, Thoughts, Quotes, Passages)
- **Content Tables**: Each section shows Title, Preview, and Last Updated date

## Actions

- **Expand/Collapse Sections**: Tap section headers to show/hide content
- **View Details**: Tap any favorite to view its full detail page
- **Remove Favorite**: Use the star button on detail pages to unfavorite items
- **Empty State**: If no favorites exist, you'll see a message encouraging you to explore content

## Tips

- Favorites are private to your account
- Use favorites to build your personal wisdom library
- Access favorites quickly from your Profile page
`
    },
    "home": {
        "title": "Home Screen Help",
        "content": `# Home Screen Help

## Purpose
The central hub of The Book of Wisdom, offering quick access to all major collections and personalized dashboard features.

## Views & Areas
- **Header**: Contains the App Title and access to Settings.
- **Dashboard Carousel**: Swipe left/right to access quick summary cards.
- **Quick Links**: Direct buttons to Topics, Thoughts, Quotes, Passages.
- **Navigation Bar**: Bottom bar for global navigation.

## Actions
- **Swipe**: Move between dashboard widgets.
- **Tap Cards**: Navigate to specific collections.
- **Bottom Navigation**: Switch between Home, Profile, and Help.
`
    },
    "login": {
        "title": "Login Help",
        "content": `# Login Help

## Purpose
Sign in to access your account and premium features.

## Options
- **Email and Password**: Traditional login
- **Social Authentication**: Google, Apple Sign-In
- **Create Account**: Register a new account

## Benefits
- Bookmark favorite content
- Track reading progress
- Access premium features
- Sync across devices
`
    },
    "notes": {
        "title": "Notes Help",
        "content": `# Notes Help

## Purpose

Your personal notes on wisdom content. Add, edit, and manage your thoughts on Topics, Thoughts, Quotes, and Passages.

## Views & Areas

- **Title Card**: "SAVED NOTES" with icon
- **Collapsible Sections**: Organized by content type (Topics, Thoughts, Quotes, Passages)
- **Note Tables**: Each section shows Title, Note Preview, and Last Updated date

## Actions

- **Expand/Collapse Sections**: Tap section headers to show/hide notes
- **View/Edit Note**: Tap any note to open the edit modal
- **Edit Modal Features**:
  - View the content title (gold text)
  - Edit your note text
  - Update button (changes to "Close" if no edits made)
  - Delete button (with confirmation)
  - Cancel button
- **Add Notes**: Use the note button on detail pages to add new notes
- **Empty State**: If no notes exist, you'll see a message encouraging you to start taking notes

## Tips

- Notes are private to your account
- Use notes to capture insights, questions, or reflections
- Edit or delete notes anytime from this page
- The "Update" button smartly changes to "Close" when there are no changes
`
    },
    "passage_detail": {
        "title": "Passage Detail Help",
        "content": `# Passage Detail Help

## Purpose

A focused view of a scripture Passage, providing context and connections.

## Views & Areas

- **Title Card**: Displays the Passage Alias and Name (or Reference) with Level indicator.
- **Tags Card**: Quick access to related content tags.
- **Content Card**: The full scripture text and its Title.
- **Parent Button**: Navigation to the parent topic.

## Actions

- **Navigate to Parent**: Tap the "PARENT" button to view the origin topic.
- **Tags**: Tap any tag to see other content with the same tag.
- **Breadcrumb**: Navigate back up the hierarchy.
`
    },
    "passages_table": {
        "title": "Passages Table Help",
        "content": `# Passages Table Help

## Purpose

Biblical verses and ancient texts that form the foundational wisdom of the collection.

## Columns & Data

The table displays different information based on your screen size:

### Mobile Views (Default)

- **Level**: Hierarchical depth (1-4).
- **Passage Alias**: Short title (prefix "Passage:" removed).
- **Reference**: The source citation (e.g., Book Chapter:Verse).

### Large Screens (Desktop)

Includes all mobile columns plus:

- **Passage Name**: Full descriptive name of the passage.

## Actions

- **Search**: Filter passages by Title, Name, or Book.
- **Navigate**: Tap any row to view the Passage Detail.
- **Pagination**: Navigate through the complete list of passages.
`
    },
    "profile": {
        "title": "Profile Screen Help",
        "content": `# Profile Screen Help

## Purpose

Manage your account, view your membership status, and access your personalized features.

## Views & Areas

- **My Profile Card**: Displays your nickname, email, join date, and Ko-fi supporter badge (if applicable)
- **Personalized Features**: Quick access to your Favorites and Notes
- **Account Actions**: Change nickname, logout

## Actions

- **View Favorites**: Access your saved wisdom items
- **View Notes**: Access your personal notes on content
- **Change Nickname**: Update your display name
- **Logout**: Sign out of your account

## Membership Tiers

- **Member**: All registered users (full access to all features)
- **Tipper**: Ko-fi supporters who've given a one-time tip
- **Supporter**: Ko-fi supporters with monthly subscriptions

## Tips

- Your nickname is displayed in the UserStatusBanner on all pages
- Support the platform via the "Give \$" button in the bottom navigation
- All features are available to all registered members
`
    },
    "quote_detail": {
        "title": "Quote Detail Help",
        "content": `# Quote Detail Help

## Purpose

A focused view of a single Quote, highlighting its author, source, and context.

## Views & Areas

- **Title Card**: Displays the Quote Alias and Name with Level indicator.
- **Tags Card**: Quick access to related content tags.
- **Content Card**: Th full quote text and its Title.
- **Metadata**: Author, Source, and Link information.
- **Parent Button**: Navigation to the parent topic.

## Actions

- **Navigate to Parent**: Tap the "PARENT" button to view the origin topic.
- **Tags**: Tap any tag to see other content with the same tag.
- **Link**: Tap the book link to view the source material (if available).
- **Breadcrumb**: Navigate back up the hierarchy.
`
    },
    "quotes_table": {
        "title": "Quotes Table Help",
        "content": `# Quotes Table Help

## Purpose

A library of wisdom from historical figures, authors, and public speakers.

## Columns & Data

The table displays different information based on your screen size:

### Mobile Views (Default)

- **Level**: Hierarchical depth (1-4).
- **Quote Alias**: Short title (prefix "Quote:" removed).

### Large Screens (Desktop)

Includes all mobile columns plus:

- **Quote Name**: Full descriptive name of the quote.

## Actions

- **Search**: Filter quotes by Author, Title, or Tag.
- **Navigate**: Tap any row to view the Quote Detail.
- **Pagination**: Navigate through the complete list of quotes.
`
    },
    "tag_detail": {
        "title": "Tag Detail Help",
        "content": `# Tag Detail Help

## Purpose
A collection view showing all content types tagged with a specific keyword.

## Views & Areas
- **Header**: Tag Name and total count.
- **Segmented Control**: Tabs for Topics, Thoughts, Quotes, Passages.
- **Content List**: Filtered items matching the tag.

## Actions
- **Switch Tab**: View different content types for this tag.
- **Tap Item**: Open specific content.
- **Back**: Return to Tags list.
`
    },
    "tags_table": {
        "title": "Tags Table Help",
        "content": `# Tags Table Help

## Purpose
A meta-collection of tags used to categorize and connect different pieces of content.

## Views & Areas
- **Search Bar**: Find tags by name.
- **Tag Cloud / Grid**: Visual representation of tags.
- **Count Indicators**: See how many items use each tag.

## Actions
- **Search**: Quickly find specific tags.
- **Tap Tag**: See a list of all content associated with that tag.
`
    },
    "thought_detail": {
        "title": "Thought Detail Help",
        "content": `# Thought Detail Help

## Purpose

A focused view of a single Thought, highlighting its context and connections.

## Views & Areas

- **Title Card**: Displays the Thought Alias and Name with Level indicator.
- **Tags Card**: Quick access to related content tags.
- **Content Card**: The full content of the thought, including its Title and Text.
- **Parent Button**: Navigation to the parent topic.

## Actions

- **Navigate to Parent**: Tap the "PARENT" button to view the origin topic.
- **Tags**: Tap any tag to see other content with the same tag.
- **Breadcrumb**: Navigate back up the hierarchy.
`
    },
    "thoughts_table": {
        "title": "Thoughts Table Help",
        "content": `# Thoughts Table Help

## Purpose

Explore the collection of original thoughts and proverbs from the author.

## Columns & Data

The table displays different information based on your screen size:

### Mobile Views (Default)

- **Level**: Hierarchical depth (1-4).
- **Thought Alias**: Short title (prefix "Thought:" removed).

### Large Screens (Desktop)

Includes all mobile columns plus:

- **Thought Name**: Full descriptive name of the thought.

## Actions

- **Search**: Filter thoughts by Alias or Name.
- **Navigate**: Tap any row to view the Thought Detail.
- **Pagination**: Navigate through the complete list of thoughts.
`
    },
    "topic_detail": {
        "title": "Topic Detail Help",
        "content": `# Topic Detail Help

## Purpose

An in-depth view of a specific Topic, aggregating all related thoughts, quotes, and passages.

## Views & Areas

- **Title Card**: Displays the Topic Alias and Name (prefixes removed) with Level indicator.
- **Tags Card**: Quick access to related content tags.
- **Description Card**: Full description togglable by language (if available).
- **Parent Button**: Large navigation button to the parent topic (if applicable).
- **Child Nodes Card**: Collapsible sections for Sub-Topics, Thoughts, Quotes, and Passages.

## Actions

- **Navigate to Parent**: Tap the large "PARENT" button to move up the hierarchy.
- **Expand/Collapse**: Tap the section headers (Sub-Topics, Thoughts, etc.) to reveal content.
- **Navigate to Child**: Tap any row in the child tables to view its detail page.
- **Tags**: Tap a tag to filter content by that specific tag.
- **Breadcrumb**: Use the top navigation bar to jump up the hierarchy.
`
    },
    "topics_table": {
        "title": "Topics Table Help",
        "content": `# Topics Table Help

## Purpose

Browse the complete catalog of wisdom topics, organized hierarchically by level.

## Columns & Data

The table displays different information based on your screen size:

### Mobile Views (Default)

- **Level**: The hierarchical depth of the topic (0-4).
- **Topic Alias**: The short, display-friendly title (prefixes removed).
- **Child Nodes**: Total count of all content within this topic (sub-topics + thoughts + quotes + passages).

### Large Screens (Desktop)

Includes all mobile columns plus:

- **Topic Name**: The full descriptive name of the topic.
- **Child Topics**: (Large Screens only) Specific count of direct sub-topics.

## Actions

- **Search**: Filter topics by Alias or Name.
- **Navigate**: Tap any row to view the Topic Detail, including its description and full content breakdown.
- **Pagination**: Use the controls at the bottom to navigate through the list.
`
    },
};
